import React, { useState, useEffect, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";

/* ─────────────────────────────────────────────────────────────────
  File type helpers
───────────────────────────────────────────────────────────────── */
const FILE_TYPES = {
  pdf: { icon: "ri-file-pdf-2-fill", color: "#e74c3c", label: "PDF" },
  doc: { icon: "ri-file-word-fill", color: "#2980b9", label: "Word" },
  docx: { icon: "ri-file-word-fill", color: "#2980b9", label: "Word" },
  xls: { icon: "ri-file-excel-fill", color: "#27ae60", label: "Excel" },
  xlsx: { icon: "ri-file-excel-fill", color: "#27ae60", label: "Excel" },
  jpg: { icon: "ri-image-fill", color: "#8e44ad", label: "Image" },
  jpeg: { icon: "ri-image-fill", color: "#8e44ad", label: "Image" },
  png: { icon: "ri-image-fill", color: "#8e44ad", label: "Image" },
  webp: { icon: "ri-image-fill", color: "#8e44ad", label: "Image" },
};

const getFileType = (filename = "") => {
  const ext = filename.split(".").pop().toLowerCase();
  return FILE_TYPES[ext] || { icon: "ri-file-fill", color: "#7f8c8d", label: ext.toUpperCase() };
};

const formatBytes = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const isImage = (filename = "") =>
  ["jpg", "jpeg", "png", "webp", "gif"].includes(filename.split(".").pop().toLowerCase());

const JobDocumentModal = ({ show, job, onClose }) => {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);   // dynamic from API
  const [loading, setLoading] = useState(false);
  const [catLoading, setCatLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("");
  const [caption, setCaption] = useState("");
  const [preview, setPreview] = useState(null);   // { name, size }
  const [lightbox, setLightbox] = useState(null);   // url string
  const fileInputRef = useRef();

  /* ── fetch doc categories ── */
  const fetchCategories = useCallback(async () => {
    setCatLoading(true);
    try {
      const res = await post("masters/doc_category/list");
      if (res.status === 200 && res.data?.length > 0) {
        setCategories(res.data.map((c) => ({ value: c.doc_category_id, label: c.category_name })));
        setDocType(String(res.data[0].doc_category_id));
      }
    } catch { /* silently ignore */ }
    finally { setCatLoading(false); }
  }, []);

  /* ── fetch documents ── */
  const fetchDocs = useCallback(async () => {
    if (!job) return;
    setLoading(true);
    try {
      const res = await post("all_jobs/job/documents", { job_id: job.job_id });
      setDocs(res.status === 200 ? res.data : []);
    } catch { setDocs([]); }
    finally { setLoading(false); }
  }, [job]);

  useEffect(() => {
    if (show && job) {
      fetchCategories();
      fetchDocs();
      setPreview(null);
      setCaption("");
    }
  }, [show, job, fetchDocs, fetchCategories]);

  /* ── file pick ── */
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview({ name: file.name, size: file.size });
  };

  /* ── upload ── */
  const handleUpload = async () => {
    const file = fileInputRef.current?.files[0];
    if (!file) {
      Swal.fire({ icon: "warning", title: "No file selected", timer: 1800, showConfirmButton: false });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("document", file);
      fd.append("job_id", job.job_id);
      fd.append("bl_number", job.bl_number);
      fd.append("doc_type", docType);
      fd.append("caption", caption);

      const data = await post("all_jobs/job/upload_document", fd);

      if (data.status === 200) {
        Swal.fire({ icon: "success", title: "Uploaded!", timer: 1500, showConfirmButton: false });
        setPreview(null);
        setCaption("");
        setDocType(categories[0]?.value ?? "");
        if (fileInputRef.current) fileInputRef.current.value = "";
        fetchDocs();
      } else {
        Swal.fire({ icon: "error", title: "Upload Failed", text: data.message });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    } finally {
      setUploading(false);
    }
  };

  /* ── delete ── */
  const handleDelete = async (doc_id) => {
    const result = await Swal.fire({
      icon: "warning", title: "Delete document?",
      text: "This cannot be undone.",
      showCancelButton: true,
      confirmButtonColor: "#d33", confirmButtonText: "Yes, delete",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await post("all_jobs/job/delete_document", { doc_id });
      if (res.status === 200) {
        Swal.fire({ icon: "success", title: "Deleted", timer: 1200, showConfirmButton: false });
        fetchDocs();
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    }
  };

  if (!show) return null;

  /* ── group by doc_type ── */
  const grouped = docs.reduce((acc, d) => {
    const key = d.doc_type || "Other";
    if (!acc[key]) acc[key] = [];
    acc[key].push(d);
    return acc;
  }, {});

  return (
    <>
      {/* Backdrop */}
      <div className="modal-backdrop fade show" style={{ zIndex: 1055 }} onClick={onClose} />

      {/* Modal */}
      <div className="modal fade show d-block" style={{ zIndex: 1056 }} tabIndex="-1">
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content" style={{ borderRadius: 12, overflow: "hidden" }}>

            {/* ── Header ── */}
            <div className="modal-header" style={{ background: "linear-gradient(135deg,#1a3c6e,#2563eb)", color: "#fff" }}>
              <div>
                <h5 className="modal-title mb-0">
                  <i className="ri-folder-open-fill me-2" />
                  Job Documents
                </h5>
                {job && (
                  <small style={{ opacity: 0.85 }}>
                    BL: <strong>{job.bl_number}</strong>
                    &nbsp;·&nbsp;
                    {job.consignees_name || ""}
                  </small>
                )}
              </div>
              <button type="button" className="btn-close btn-close-white" onClick={onClose} />
            </div>

            {/* ── Body ── */}
            <div className="modal-body" style={{ background: "#f1f5f9", minHeight: 400 }}>

              {/* Upload card */}
              <div className="card mb-4 shadow-sm border-0">
                <div
                  className="card-header py-2"
                  style={{ background: "#fff", borderBottom: "2px solid #e2e8f0" }}
                >
                  <span className="fw-semibold text-muted" style={{ fontSize: 13 }}>
                    <i className="ri-upload-cloud-2-line me-1 text-primary" />
                    Upload New Document
                  </span>
                </div>
                <div className="card-body" style={{ background: "#fff" }}>
                  <div className="row g-3 align-items-end">

                    {/* File */}
                    <div className="col-md-3">
                      <label className="form-label fw-semibold" style={{ fontSize: 12 }}>
                        File <span className="text-danger">*</span>
                      </label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="form-control form-control-sm"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp"
                        onChange={handleFileChange}
                      />
                      <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3 }}>
                        PDF, Word, Excel, Images accepted
                      </div>
                    </div>

                    {/* Doc type — dynamic from API */}
                    <div className="col-md-3">
                      <label className="form-label fw-semibold" style={{ fontSize: 12 }}>
                        Document Type <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select form-select-sm"
                        value={docType}
                        onChange={(e) => setDocType(e.target.value)}
                        disabled={catLoading}
                      >
                        {catLoading ? (
                          <option>Loading…</option>
                        ) : categories?.length === 0 ? (
                          <option value="">No categories found</option>
                        ) : (
                          categories.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))
                        )}
                      </select>
                    </div>

                    {/* Caption */}
                    <div className="col-md-3">
                      <label className="form-label fw-semibold" style={{ fontSize: 12 }}>
                        Note <span className="text-muted fw-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="e.g. Original copy, Signed version…"
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                      />
                    </div>

                    {/* Upload btn */}
                    <div className="col-md-2">
                      <button
                        className="btn btn-primary btn-sm w-100"
                        onClick={handleUpload}
                        disabled={uploading || !preview}
                      >
                        {uploading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" />
                            Uploading…
                          </>
                        ) : (
                          <>
                            <i className="ri-upload-line me-1" />
                            Upload
                          </>
                        )}
                      </button>
                    </div>

                    {/* Selected file preview */}
                    {preview && (
                      <div className="col-md-1">
                        <div
                          style={{
                            display: "flex", flexDirection: "column",
                            alignItems: "center", gap: 4,
                          }}
                        >
                          <i
                            className={getFileType(preview.name).icon}
                            style={{ fontSize: 28, color: getFileType(preview.name).color }}
                          />
                          <span style={{ fontSize: 9, color: "#64748b", textAlign: "center", wordBreak: "break-all" }}>
                            {preview.name?.length > 16
                              ? preview.name.slice(0, 14) + "…"
                              : preview.name}
                          </span>
                          <span style={{ fontSize: 9, color: "#94a3b8" }}>
                            {formatBytes(preview.size)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Documents list */}
              <div className="card shadow-sm border-0">
                <div
                  className="card-header py-2 d-flex justify-content-between align-items-center"
                  style={{ background: "#fff", borderBottom: "2px solid #e2e8f0" }}
                >
                  <span className="fw-semibold text-muted" style={{ fontSize: 13 }}>
                    <i className="ri-file-list-3-line me-1 text-primary" />
                    Uploaded Documents
                  </span>
                  <span className="badge bg-primary rounded-pill">{docs.length}</span>
                </div>
                <div className="card-body p-3" style={{ background: "#fff" }}>

                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" />
                      <div className="mt-2 text-muted" style={{ fontSize: 13 }}>Loading documents…</div>
                    </div>
                  ) : docs?.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="ri-folder-open-line" style={{ fontSize: 52, opacity: 0.25 }} />
                      <div className="mt-2" style={{ fontSize: 13 }}>No documents uploaded yet.</div>
                    </div>
                  ) : (
                    /* Grouped by doc_type */
                    Object.entries(grouped).map(([type, items]) => (
                      <div key={type} className="mb-4">
                        {/* Group header */}
                        <div
                          className="d-flex align-items-center gap-2 mb-2"
                          style={{ borderLeft: "3px solid #2563eb", paddingLeft: 10 }}
                        >
                          <span className="fw-bold" style={{ fontSize: 13, color: "#1e40af" }}>
                            {type}
                          </span>
                          <span className="badge bg-light text-dark border" style={{ fontSize: 10 }}>
                            {items?.length}
                          </span>
                        </div>

                        {/* Document rows */}
                        <div className="d-flex flex-column gap-2">
                          {items.map((doc) => {
                            const ft = getFileType(doc.file_name);
                            const img = isImage(doc.file_name);
                            return (
                              <div
                                key={doc.doc_id}
                                className="d-flex align-items-center gap-3 p-2 rounded"
                                style={{
                                  background: "#f8fafc",
                                  border: "1px solid #e2e8f0",
                                  transition: "box-shadow 0.15s",
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.boxShadow = "none")
                                }
                              >
                                {/* File icon / thumbnail */}
                                <div
                                  style={{
                                    width: 44, height: 44, borderRadius: 8, flexShrink: 0,
                                    background: ft.color + "15",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: img ? "pointer" : "default",
                                  }}
                                  onClick={() => img && setLightbox(doc.file_path)}
                                >
                                  {img ? (
                                    <img
                                      src={doc.file_path}
                                      alt=""
                                      style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 8 }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display = "block";
                                      }}
                                    />
                                  ) : null}
                                  <i
                                    className={ft.icon}
                                    style={{
                                      fontSize: 22, color: ft.color,
                                      display: img ? "none" : "block",
                                    }}
                                  />
                                </div>

                                {/* Info */}
                                <div className="flex-fill" style={{ minWidth: 0 }}>
                                  <div
                                    className="fw-semibold text-truncate"
                                    style={{ fontSize: 13, color: "#0f172a" }}
                                    title={doc.file_name}
                                  >
                                    {doc.caption || doc.file_name}
                                  </div>
                                  <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                                    <span
                                      className="badge"
                                      style={{
                                        background: ft.color + "20",
                                        color: ft.color,
                                        fontSize: 9,
                                        fontWeight: 600,
                                        padding: "2px 6px",
                                      }}
                                    >
                                      {ft.label}
                                    </span>
                                    <span style={{ fontSize: 10, color: "#94a3b8" }}>
                                      {formatBytes(doc.file_size)}
                                    </span>
                                    <span style={{ fontSize: 10, color: "#94a3b8" }}>
                                      {doc.date_added
                                        ? new Date(doc.date_added).toLocaleDateString("en-IN", {
                                          day: "2-digit", month: "short", year: "numeric",
                                        })
                                        : "—"}
                                    </span>
                                    {doc.caption && (
                                      <span style={{ fontSize: 10, color: "#64748b", fontStyle: "italic" }}>
                                        {doc.caption}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Actions */}
                                <div className="d-flex gap-2 flex-shrink-0">
                                  <a
                                    href={doc.file_path}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn btn-sm btn-outline-primary"
                                    style={{ fontSize: 11, padding: "3px 10px" }}
                                    title="View / Download"
                                  >
                                    <i className="ri-download-2-line me-1" />
                                    Download
                                  </a>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    style={{ fontSize: 11, padding: "3px 10px" }}
                                    title="Delete"
                                    onClick={() => handleDelete(doc.doc_id)}
                                  >
                                    <i className="ri-delete-bin-line" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer" style={{ background: "#f8fafc" }}>
              <small className="text-muted me-auto" style={{ fontSize: 11 }}>
                <i className="ri-information-line me-1" />
                PDF · Word · Excel · Images (max 10 MB per file)
              </small>
              <button className="btn btn-secondary btn-sm" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox for image docs */}
      {lightbox && (
        <div
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.88)",
            zIndex: 2000, display: "flex",
            alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setLightbox(null)}
        >
          <button
            style={{
              position: "absolute", top: 18, right: 24,
              background: "none", border: "none",
              color: "#fff", fontSize: 32, cursor: "pointer",
            }}
            onClick={() => setLightbox(null)}
          >
            <i className="ri-close-line" />
          </button>
          <img
            src={lightbox} alt="Full view"
            style={{
              maxWidth: "90vw", maxHeight: "90vh",
              borderRadius: 10,
              boxShadow: "0 10px 60px rgba(0,0,0,0.6)",
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default JobDocumentModal;
